import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] },
};

function ServiceCard({ tag, title, desc, items, image, index }) {
  return (
    <motion.article
      {...fade}
      transition={{ ...fade.transition, delay: index * 0.15 }}
      className="group relative border border-[#27272A] bg-[#0a0a0a] p-8 md:p-10 flex flex-col overflow-hidden"
      data-testid={`service-card-${tag.toLowerCase()}`}
    >
      {/* Image clipped frame */}
      <div className="relative aspect-[16/10] overflow-hidden border border-[#27272A] mb-8">
        <motion.img
          src={image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "grayscale(0.4) contrast(1.05) brightness(0.75)" }}
          whileHover={{ scale: 1.05, filter: "grayscale(0) contrast(1.1) brightness(0.9)" }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        />
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#050505]/80 backdrop-blur-sm border border-[#27272A] px-3 py-1.5">
          <span className="w-1.5 h-1.5 bg-[#E1FF00]" />
          <span className="font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#F4F4F5]">{tag}</span>
        </div>
      </div>

      <h3 className="font-display text-3xl md:text-4xl text-[#F4F4F5] tracking-tighter leading-[0.95]">
        {title}
      </h3>
      <p className="mt-4 text-[#A1A1AA] text-sm md:text-base max-w-md">{desc}</p>

      <ul className="mt-8 space-y-3 border-t border-[#27272A] pt-6">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="font-mono-acc text-[10px] text-[#E1FF00] mt-1.5 shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-[#F4F4F5] text-sm md:text-base">{item}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

export default function Services() {
  const { t } = useI18n();
  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#050505]" data-testid="services-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div {...fade} className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA]">
              <span className="w-8 h-px bg-[#E1FF00]" />
              {t.services.overline}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#F4F4F5] tracking-tighter leading-[0.95]">
              {t.services.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base md:text-lg max-w-xl">
              {t.services.subtitle}
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <ServiceCard
            index={0}
            tag={t.services.commercial.tag}
            title={t.services.commercial.title}
            desc={t.services.commercial.desc}
            items={t.services.commercial.items}
            image="https://images.pexels.com/photos/164586/pexels-photo-164586.jpeg"
          />
          <ServiceCard
            index={1}
            tag={t.services.industrial.tag}
            title={t.services.industrial.title}
            desc={t.services.industrial.desc}
            items={t.services.industrial.items}
            image="https://images.unsplash.com/photo-1589109807644-924edf14ee09"
          />
        </div>
      </div>
    </section>
  );
}
